import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Card, 
  Button, 
  Table, 
  Space, 
  Modal, 
  message, 
  Popconfirm, 
  Tag, 
  Image, 
  Row, 
  Col,
  Input,
  Select,
  Form,
  Upload,
  Divider,
  Typography
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  UploadOutlined,
  SaveOutlined,
  CloseOutlined,
  MedicineBoxOutlined,
  MailOutlined,
  PhoneOutlined
} from '@ant-design/icons';
import { 
  fetchSpecialities, 
  addSpeciality, 
  updateSpeciality, 
  deleteSpeciality 
} from '../../actions/DepartmentManagementAction';
import RichTextEditor from './RichTextEditor';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const SpecialityManagement = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedSpeciality, setSelectedSpeciality] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewSpeciality, setViewSpeciality] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { specialities, specialityLoading, specialityError } = useSelector(state => state.departmentManagement);

  // Fetch specialities on component mount
  useEffect(() => {
    fetchSpecialitiesList();
  }, []);

  // Refetch specialities when status filter changes
  useEffect(() => {
    fetchSpecialitiesList(statusFilter);
  }, [statusFilter]);

  const fetchSpecialitiesList = async (status = 'All') => {
    try {
      setLoading(true);
      await dispatch(fetchSpecialities(status));
    } catch (error) {
      console.error('Error fetching specialities:', error);
      message.error('Failed to fetch specialities');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter('All');
    fetchSpecialitiesList('All');
  };

  const handleAdd = () => {
    setModalMode('add');
    setSelectedSpeciality(null);
    form.resetFields();
    form.setFieldsValue({
      status: 'Active'
    });
    setModalVisible(true);
  };

  const handleEdit = (speciality) => {
    setModalMode('edit');
    setSelectedSpeciality(speciality);
    form.setFieldsValue({
      specialityName: speciality.specialityName,
      specialityImage: speciality.specialityImage,
      specialityDescription: speciality.specialityDescription,
      specialityUrl: speciality.specialityUrl,
      specialityNote: speciality.specialityNote,
      specialityEmail: speciality.specialityEmail,
      specialityPhone: speciality.specialityPhone,
      status: speciality.specialityStatus || speciality.status || 'Active'
    });
    setModalVisible(true);
  };

  const handleView = (speciality) => {
    setViewSpeciality(speciality);
    setViewModalVisible(true);
  };

  const handleDelete = async (specialityId) => {
    try {
      setLoading(true);
      await dispatch(deleteSpeciality(specialityId));
      message.success('Speciality deleted successfully');
      fetchSpecialitiesList();
    } catch (error) {
      console.error('Error deleting speciality:', error);
      message.error('Failed to delete speciality');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      const specialityData = {
        ...values,
        specialityDescription: values.specialityDescription || ''
      };

      // Debug: Log the data being sent
      console.log('Form values:', values);
      console.log('Speciality data being sent:', specialityData);

      if (modalMode === 'edit' && selectedSpeciality) {
        specialityData.specialityId = selectedSpeciality.specialityId || selectedSpeciality.SpecialityId;
        console.log('Updating speciality with data:', specialityData);
        await dispatch(updateSpeciality(specialityData));
        message.success('Speciality updated successfully');
      } else {
        console.log('Adding new speciality with data:', specialityData);
        await dispatch(addSpeciality(specialityData));
        message.success('Speciality added successfully');
      }
      
      setModalVisible(false);
      fetchSpecialitiesList();
    } catch (error) {
      console.error('Error saving speciality:', error);
      message.error('Failed to save speciality');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    setSelectedSpeciality(null);
    form.resetFields();
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  // Filter specialities based on search text and status
  const filteredSpecialities = specialities.filter(speciality => {
    const matchesSearch = speciality.specialityName?.toLowerCase().includes(searchText.toLowerCase()) ||
                         speciality.specialityDescription?.toLowerCase().includes(searchText.toLowerCase()) ||
                         speciality.specialityEmail?.toLowerCase().includes(searchText.toLowerCase()) ||
                         speciality.specialityPhone?.toLowerCase().includes(searchText.toLowerCase());
    
    const currentStatus = speciality.specialityStatus || speciality.status || 'Active';
    const matchesStatus = statusFilter === 'All' || currentStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Calculate total pages for pagination
  const totalPages = Math.ceil(filteredSpecialities.length / pageSize);

    const columns = [
    {
      title: 'Speciality Name',
      dataIndex: 'specialityName',
      key: 'specialityName',
      render: (text) => <Text strong>{text}</Text>,
      sorter: (a, b) => a.specialityName?.localeCompare(b.specialityName),
    },
    {
      title: 'Image',
      dataIndex: 'specialityImage',
      key: 'specialityImage',
      render: (imageUrl) => (
        imageUrl ? (
          <Image
            width={50}
            height={50}
            src={imageUrl}
            alt="Speciality"
            style={{ objectFit: 'cover', borderRadius: '4px' }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
          />
        ) : (
          <div style={{ 
            width: 50, 
            height: 50, 
            backgroundColor: '#f0f0f0', 
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
              color: '#999'
          }}>
            No Image
          </div>
        )
      ),
    },
    {
      title: 'Email',
      dataIndex: 'specialityEmail',
      key: 'specialityEmail',
      render: (email) => (
        <div style={{ maxWidth: 150 }}>
          {email ? (
            <Text ellipsis={{ tooltip: email }}>
              <MailOutlined style={{ marginRight: 4, color: '#1890ff' }} />
              {email}
            </Text>
          ) : (
            <Text type="secondary">No email</Text>
          )}
        </div>
      ),
      sorter: (a, b) => (a.specialityEmail || '').localeCompare(b.specialityEmail || ''),
    },
    {
      title: 'Phone',
      dataIndex: 'specialityPhone',
      key: 'specialityPhone',
      render: (phone) => (
        <div style={{ maxWidth: 120 }}>
          {phone ? (
            <Text ellipsis={{ tooltip: phone }}>
              <PhoneOutlined style={{ marginRight: 4, color: '#52c41a' }} />
              {phone}
            </Text>
          ) : (
            <Text type="secondary">No phone</Text>
          )}
        </div>
      ),
      sorter: (a, b) => (a.specialityPhone || '').localeCompare(b.specialityPhone || ''),
    },
    {
      title: 'Description',
      dataIndex: 'specialityDescription',
      key: 'specialityDescription',
      render: (text) => (
        <div style={{ maxWidth: 200 }}>
          {text ? (
            <Text ellipsis={{ tooltip: text }}>
              {text}
            </Text>
          ) : (
            <Text type="secondary">No description</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'specialityStatus',
      key: 'specialityStatus',
      render: (status, record) => {
        const currentStatus = status || record.status || 'Active';
        return (
          <Tag color={currentStatus === 'Active' ? 'green' : 'red'}>
            {currentStatus}
          </Tag>
        );
      },
      filters: [
        { text: 'Active', value: 'Active' },
        { text: 'Inactive', value: 'Inactive' }
      ],
      onFilter: (value, record) => (record.specialityStatus || record.status || 'Active') === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            title="View Details"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit Speciality"
          />
          <Popconfirm
            title="Delete Speciality"
            description="Are you sure you want to delete this speciality? This action cannot be undone."
            onConfirm={() => handleDelete(record.specialityId || record.SpecialityId)}
            okText="Yes, Delete"
            cancelText="Cancel"
            placement="topRight"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete Speciality"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="speciality-management-container">
      <Card 
       
        className="speciality-management-main-card"
       
      >
        <div className="speciality-management">
          <Card 
            className="speciality-management-card"
          >
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Input.Search
              placeholder="Search specialities..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              allowClear={false}
            >
              <Option value="All">All Status</Option>
              <Option value="Active">Active</Option>
              <Option value="Inactive">Inactive</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button 
              onClick={handleClearFilters}
              style={{ width: '100%' }}
              disabled={searchText === '' && statusFilter === 'All'}
            >
              Clear Filters
            </Button>
          </Col>
          <Col xs={24} sm={24} md={6} style={{ textAlign: 'right' }}>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleAdd}
              size="large"
            >
              Add Speciality
            </Button>
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={filteredSpecialities}
          rowKey={(record) => record.specialityId || record.SpecialityId}
          loading={loading}
          pagination={totalPages > 1 ? {
            
            total: filteredSpecialities.length,
            
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} specialities`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          } : false}
          scroll={{ x: 1200 }}
        />
          </Card>
        </div>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={`${modalMode === 'add' ? 'Add New' : 'Edit'} Speciality`}
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
        destroyOnClose
        className="speciality-modal"
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            status: 'Active'
          }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="specialityName"
                label="Speciality Name"
                rules={[
                  { required: true, message: 'Please enter speciality name' },
                  { max: 255, message: 'Speciality name cannot exceed 255 characters' }
                ]}
              >
                <Input placeholder="Enter speciality name" />
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[
                  { required: true, message: 'Please select status' }
                ]}
              >
                <Select placeholder="Select status">
                  <Option value="Active">Active</Option>
                  <Option value="Inactive">Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="specialityEmail"
                label="Email Address"
                rules={[
                  { type: 'email', message: 'Please enter a valid email address' },
                  { max: 255, message: 'Email cannot exceed 255 characters' }
                ]}
              >
                <Input 
                  placeholder="Enter email address" 
                  prefix={<MailOutlined style={{ color: '#1890ff' }} />}
                />
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={12}>
              <Form.Item
                name="specialityPhone"
                label="Phone Number"
                rules={[
                  { max: 20, message: 'Phone number cannot exceed 20 characters' },
                  { pattern: /^[\+]?[0-9]+\s?[0-9]+$/, message: 'Please enter a valid phone number (only numbers, + sign, and one space allowed)' }
                ]}
              >
                <Input 
                  placeholder="Enter phone number" 
                  prefix={<PhoneOutlined style={{ color: '#52c41a' }} />}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="specialityImage"
            label="Speciality Image URL"
            rules={[
              { max: 500, message: 'Image URL cannot exceed 500 characters' }
            ]}
          >
            <Input placeholder="Enter image URL or file path" />
          </Form.Item>

          <Form.Item
            name="image_upload"
            label="Upload Image"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            style={{ display: 'none' }}
          >
            <Upload
              name="image"
              listType="picture"
              maxCount={1}
              beforeUpload={() => false}
            >
              <Button icon={<UploadOutlined />}>Upload Image</Button>
            </Upload>
          </Form.Item>

          <Form.Item
            name="specialityUrl"
            label="Speciality URL"
            rules={[
              { max: 255, message: 'URL cannot exceed 255 characters' }
            ]}
          >
            <Input placeholder="Enter speciality URL (e.g., cardiology)" />
          </Form.Item>

          <Form.Item
            name="specialityNote"
            label="Speciality Note"
            rules={[
              { max: 1000, message: 'Note cannot exceed 1000 characters' }
            ]}
          >
            <TextArea 
              placeholder="Enter additional notes about the speciality"
              rows={3}
              maxLength={1000}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="specialityDescription"
            label="Speciality Description"
            rules={[
              { max: 5000, message: 'Description cannot exceed 5000 characters' }
            ]}
          >
            <RichTextEditor 
              placeholder="Enter speciality description"
              rows={6}
            />
          </Form.Item>

          <div className="modal-footer" style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: '12px',
            marginTop: '24px'
          }}>
            <Button 
              onClick={handleCancel} 
              icon={<CloseOutlined />}
              size="large"
              style={{ flex: 1 }}
            >
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              icon={<SaveOutlined />}
              size="large"
              style={{ flex: 1 }}
            >
              {modalMode === 'add' ? 'Add Speciality' : 'Update Speciality'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* View Modal */}
      <Modal
        title="Speciality Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
        className="speciality-view-modal"
        style={{ top: 20 }}
      >
        {viewSpeciality && (
          <div className="speciality-details">
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  {viewSpeciality.specialityImage ? (
                    <Image
                      width={200}
                      height={200}
                      src={viewSpeciality.specialityImage}
                      alt={viewSpeciality.specialityName}
                      style={{ objectFit: 'cover', borderRadius: '8px' }}
                      fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                    />
                  ) : (
                    <div style={{ 
                      width: 200, 
                      height: 200, 
                      backgroundColor: '#f0f0f0', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#999'
                    }}>
                      No Image Available
                    </div>
                  )}
                </div>
              </Col>
              <Col xs={24} sm={16}>
                <div style={{ marginBottom: 16 }}>
                  <Title level={4} style={{ marginBottom: 8 }}>
                    {viewSpeciality.specialityName}
                  </Title>
                  <Tag color={(viewSpeciality.specialityStatus || viewSpeciality.status) === 'Active' ? 'green' : 'red'}>
                    {viewSpeciality.specialityStatus || viewSpeciality.status || 'Active'}
                  </Tag>
                </div>
                
                {viewSpeciality.specialityDescription && (
                  <div style={{ marginBottom: 16 }}>
                    <Title level={5}>Description</Title>
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: viewSpeciality.specialityDescription 
                      }}
                      style={{ 
                        backgroundColor: '#fafafa', 
                        padding: 12, 
                        borderRadius: 4,
                        maxHeight: 200,
                        overflow: 'auto'
                      }}
                    />
                  </div>
                )}

                {viewSpeciality.specialityUrl && (
                  <div style={{ marginBottom: 16 }}>
                    <Title level={5}>Speciality URL</Title>
                    <Text code>{viewSpeciality.specialityUrl}</Text>
                  </div>
                )}

                {viewSpeciality.specialityNote && (
                  <div style={{ marginBottom: 16 }}>
                    <Title level={5}>Speciality Note</Title>
                    <Text>{viewSpeciality.specialityNote}</Text>
                  </div>
                )}

                {viewSpeciality.specialityEmail && (
                  <div style={{ marginBottom: 16 }}>
                    <Title level={5}>Email Address</Title>
                    <Text>
                      <MailOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                      {viewSpeciality.specialityEmail}
                    </Text>
                  </div>
                )}

                {viewSpeciality.specialityPhone && (
                  <div style={{ marginBottom: 16 }}>
                    <Title level={5}>Phone Number</Title>
                    <Text>
                      <PhoneOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                      {viewSpeciality.specialityPhone}
                    </Text>
                  </div>
                )}
                
                <div>
                  <Title level={5}>Speciality ID</Title>
                  <Text code>{viewSpeciality.specialityId || viewSpeciality.SpecialityId}</Text>
                </div>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SpecialityManagement;
